import React, { useState, useEffect, useMemo } from 'react';
import {
    Plus,
    Truck,
    CheckCircle2,
    Trash2,
    AlertCircle,
    FileText,
    History as HistoryIcon,
    ArrowLeft,
    Package,
    Calendar,
    Edit
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../../components/ui/select';
import api from '../../utils/api';
import { cn } from '../../components/ui/utils';
import { toast } from 'sonner';

interface NewIncomingOrderProps {
    onBack: () => void;
    onRefresh: () => void;
    order?: any;
}

const NewIncomingOrder: React.FC<NewIncomingOrderProps> = ({ onBack, onRefresh, order }) => {
    const isEdit = !!order;
    const [formData, setFormData] = useState<any>({
        supplierId: '',
        deliveryLocationId: '',
        expectedDeliveryDate: '',
        poNumber: `PO-${new Date().getFullYear()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
        isUrgent: false,
        orderNotes: '',
        items: [{ inventoryItemId: '', quantityOrdered: 1, unitCost: 0, itemNotes: '' }]
    });

    const [suppliers, setSuppliers] = useState<any[]>([]);
    const [locations, setLocations] = useState<any[]>([]);
    const [inventoryItems, setInventoryItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [metaLoading, setMetaLoading] = useState(true);

    useEffect(() => {
        const fetchMetadata = async () => {
            try {
                setMetaLoading(true);
                const [s, l, i] = await Promise.all([
                    api.get('/api/inventory/suppliers'),
                    api.get('/api/inventory/locations'),
                    api.get('/api/inventory/items')
                ]);
                setSuppliers(s);
                setLocations(l);
                setInventoryItems(i);

                if (isEdit) {
                    const fullOrder = await api.get(`/api/inventory/orders/${order.id}`);

                    // Format date for input field using local time to avoid timezone shifts
                    let formattedDate = '';
                    if (fullOrder.expectedDeliveryDate) {
                        const date = new Date(fullOrder.expectedDeliveryDate);
                        formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                    }

                    setFormData({
                        supplierId: fullOrder.supplier?.id || '',
                        deliveryLocationId: fullOrder.deliveryLocation?.id || '',
                        expectedDeliveryDate: formattedDate,
                        poNumber: fullOrder.poNumber || '',
                        isUrgent: fullOrder.isUrgent || false,
                        orderNotes: fullOrder.orderNotes || '',
                        items: fullOrder.items.map((it: any) => ({
                            inventoryItemId: it.inventoryItem?.id || '',
                            quantityOrdered: it.quantityOrdered,
                            unitCost: parseFloat(it.unitCost),
                            itemNotes: it.itemNotes || ''
                        }))
                    });
                }
            } catch (err) {
                console.error('Error fetching metadata:', err);
                toast.error('Failed to load order data');
            } finally {
                setMetaLoading(false);
            }
        };
        fetchMetadata();
    }, [isEdit, order]);

    const filteredInventoryItems = useMemo(() => {
        if (!inventoryItems.length) return [];

        // Filter for active, non-archived items only
        const activeItems = inventoryItems.filter(item => item.status === 'Active' && !item.archived);

        const map = new Map<string, any>();

        // Sort items: items in the selected delivery location come first
        const sorted = [...activeItems].sort((a, b) => {
            const aInLoc = a.location?.id === formData.deliveryLocationId ? 1 : 0;
            const bInLoc = b.location?.id === formData.deliveryLocationId ? 1 : 0;
            return bInLoc - aInLoc;
        });

        for (const item of sorted) {
            if (!map.has(item.sku)) {
                map.set(item.sku, item);
            }
        }

        return Array.from(map.values());
    }, [inventoryItems, formData.deliveryLocationId]);

    const addItem = () => {
        setFormData({
            ...formData,
            items: [...formData.items, { inventoryItemId: '', quantityOrdered: 1, unitCost: 0, itemNotes: '' }]
        });
    };

    const removeItem = (index: number) => {
        const newItems = [...formData.items];
        newItems.splice(index, 1);
        setFormData({ ...formData, items: newItems });
    };

    const updateItem = (index: number, field: string, value: any) => {
        const newItems = [...formData.items] as any;
        newItems[index][field] = value;

        // Prepopulate unit cost when item is selected
        if (field === 'inventoryItemId') {
            const item = inventoryItems.find(i => i.id === value);
            if (item) {
                newItems[index].unitCost = parseFloat(item.costPerUnit) || 0;
            }
        }

        setFormData({ ...formData, items: newItems });
    };

    const calculateTotalCost = () => {
        return formData.items.reduce((sum: number, item: any) => sum + (item.quantityOrdered * item.unitCost), 0);
    };

    const handleSubmit = async () => {
        if (!formData.supplierId || !formData.deliveryLocationId || !formData.expectedDeliveryDate || formData.items.some((i: any) => !i.inventoryItemId)) {
            toast.error('Please fill in all required fields (Supplier, Location, Expected Date, and Items)');
            return;
        }

        try {
            setLoading(true);
            if (isEdit) {
                await api.put(`/api/inventory/orders/${order.id}`, formData);
                toast.success('Order updated successfully');
            } else {
                await api.post('/api/inventory/orders', formData);
                toast.success('Incoming order created successfully');
            }
            onRefresh();
            onBack();
        } catch (error: any) {
            console.error('Error saving order:', error);
            toast.error(error.message || 'Failed to save order');
        } finally {
            setLoading(false);
        }
    };

    if (metaLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onBack}
                        className="rounded-xl flex items-center gap-2 text-gray-500 hover:text-gray-900 -ml-2 transition-all hover:bg-gray-100"
                    >
                        <ArrowLeft size={18} />
                        Back
                    </Button>
                    <div>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                            {isEdit ? <Edit size={24} className="text-blue-500" /> : <Truck size={24} className="text-gray-900" />}
                            {isEdit ? 'Edit Incoming Order' : 'New Incoming Order'}
                        </h2>
                        <p className="text-gray-500 text-xs sm:text-sm font-medium">
                            {isEdit ? `Update details for PO ${formData.poNumber}` : 'Create a new purchase order for incoming stock'}
                        </p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column: Form Details */}
                <div className="lg:col-span-2 space-y-6">
                    {/* General Information Card */}
                    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="p-6 border-b border-gray-50 bg-gray-50/30">
                            <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                                <FileText size={16} className="text-gray-400" />
                                General Information
                            </h3>
                        </div>
                        <div className="p-6 space-y-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Supplier *</label>
                                    <Select value={formData.supplierId} onValueChange={(val) => setFormData({ ...formData, supplierId: val })}>
                                        <SelectTrigger className="rounded-xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all shadow-sm">
                                            <SelectValue placeholder="Select supplier" />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-xl border-gray-100">
                                            {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Delivery Location *</label>
                                    <Select value={formData.deliveryLocationId} onValueChange={(val) => setFormData({ ...formData, deliveryLocationId: val })}>
                                        <SelectTrigger className="rounded-xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all shadow-sm">
                                            <SelectValue placeholder="Select location" />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-xl border-gray-100">
                                            {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Expected Delivery Date *</label>
                                    <div className="relative">
                                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                        <Input
                                            type="date"
                                            className="rounded-xl border-gray-100 bg-gray-50/50 h-12 pl-10 focus:bg-white transition-all shadow-sm"
                                            value={formData.expectedDeliveryDate}
                                            min={new Date().toISOString().split('T')[0]}
                                            onChange={(e) => setFormData({ ...formData, expectedDeliveryDate: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">PO Number / Reference</label>
                                    <Input
                                        className="rounded-xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all shadow-sm font-bold"
                                        value={formData.poNumber}
                                        onChange={(e) => setFormData({ ...formData, poNumber: e.target.value })}
                                    />
                                </div>
                            </div>

                            <button
                                onClick={() => setFormData({ ...formData, isUrgent: !formData.isUrgent })}
                                className={cn(
                                    "w-full flex items-center gap-3 p-4 rounded-2xl border transition-all text-left shadow-sm",
                                    formData.isUrgent
                                        ? "bg-orange-50 border-orange-200 text-orange-700"
                                        : "bg-white border-gray-100 text-gray-500 hover:border-gray-200"
                                )}
                            >
                                <div className={cn(
                                    "w-6 h-6 rounded-lg flex items-center justify-center border transition-all",
                                    formData.isUrgent ? "bg-orange-600 border-orange-600" : "bg-gray-50 border-gray-200"
                                )}>
                                    {formData.isUrgent && <div className="w-2.5 h-2.5 bg-white rounded-sm" />}
                                </div>
                                <AlertCircle size={20} className={formData.isUrgent ? "text-orange-500" : "text-gray-300"} />
                                <div>
                                    <p className="text-sm font-bold">Mark as Urgent Order</p>
                                    <p className="text-[10px] font-medium opacity-70">Flags this order for prioritized receiving</p>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Order Items Section */}
                    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="p-6 border-b border-gray-50 bg-gray-50/30 flex items-center justify-between">
                            <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                                <Package size={16} className="text-gray-400" />
                                Order Items
                            </h3>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={addItem}
                                className="rounded-xl text-xs font-bold gap-2 h-9 border-gray-100 hover:bg-gray-50"
                            >
                                <Plus size={14} /> Add Item
                            </Button>
                        </div>
                        <div className="p-6 space-y-4">
                            {formData.items.map((item: any, idx: number) => (
                                <div key={idx} className="p-5 sm:p-6 bg-gray-50/50 rounded-[28px] border border-gray-100 space-y-4 relative group hover:shadow-md transition-all hover:bg-white hover:border-blue-100">
                                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                                        <div className="sm:col-span-6 space-y-1.5">
                                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Item Selection</label>
                                            <Select value={item.inventoryItemId} onValueChange={(val) => updateItem(idx, 'inventoryItemId', val)}>
                                                <SelectTrigger className="rounded-xl border-gray-100 bg-white h-11 shadow-sm">
                                                    <SelectValue placeholder="Select item" />
                                                </SelectTrigger>
                                                <SelectContent className="rounded-xl border-gray-100">
                                                    {filteredInventoryItems.map(i => (
                                                        <SelectItem key={i.id} value={i.id}>
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-medium">{i.name}</span>
                                                                <span className="text-[9px] text-gray-400 font-bold uppercase py-0.5 px-2 bg-gray-50 rounded-full">{i.sku}</span>
                                                                {i.location?.id === formData.deliveryLocationId && (
                                                                    <span className="text-[8px] font-black text-blue-500 uppercase bg-blue-50 px-1.5 py-0.5 rounded-md">In Selected Location</span>
                                                                )}
                                                            </div>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="sm:col-span-3 space-y-1.5">
                                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Quantity</label>
                                            <Input
                                                type="number"
                                                className="rounded-xl border-gray-100 bg-white h-11 text-center shadow-sm font-bold"
                                                value={item.quantityOrdered}
                                                onChange={(e) => updateItem(idx, 'quantityOrdered', parseInt(e.target.value) || 0)}
                                            />
                                        </div>
                                        <div className="sm:col-span-3 space-y-1.5">
                                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Unit Cost ($)</label>
                                            <Input
                                                type="number"
                                                className="rounded-xl border-gray-100 bg-white h-11 shadow-sm font-bold"
                                                value={item.unitCost}
                                                onChange={(e) => updateItem(idx, 'unitCost', parseFloat(e.target.value) || 0)}
                                            />
                                        </div>
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                                        <div className="w-full space-y-1.5">
                                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Item Notes (Optional)</label>
                                            <Input
                                                placeholder="Special instructions for this item..."
                                                className="rounded-xl border-gray-100 bg-white h-11 shadow-sm text-sm"
                                                value={item.itemNotes}
                                                onChange={(e) => updateItem(idx, 'itemNotes', e.target.value)}
                                            />
                                        </div>
                                        <div className="w-full sm:w-auto text-right shrink-0">
                                            <p className="text-[9px] font-bold text-blue-500 uppercase tracking-widest mb-0.5">Subtotal</p>
                                            <p className="text-lg font-black text-blue-600">
                                                ${(item.quantityOrdered * item.unitCost).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                            </p>
                                        </div>
                                    </div>

                                    {formData.items.length > 1 && (
                                        <button
                                            onClick={() => removeItem(idx)}
                                            className="absolute -top-3 -right-3 w-8 h-8 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-red-100 shadow-md transform hover:scale-110 active:scale-95 z-10"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    )}
                                </div>
                            ))}

                            {formData.items.length === 0 && (
                                <div className="text-center py-12 bg-gray-50/50 rounded-3xl border border-dashed border-gray-200">
                                    <Package size={40} className="mx-auto text-gray-200 mb-3" />
                                    <p className="text-sm font-medium text-gray-400">No items added to this order yet.</p>
                                    <Button variant="outline" size="sm" onClick={addItem} className="mt-4 rounded-xl">Add First Item</Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column: Summary & Actions */}
                <div className="space-y-6">
                    {/* Summary Card */}
                    <div className="bg-slate-900 rounded-[32px] p-8 text-white shadow-xl shadow-slate-200 sticky top-6">
                        <div className="flex items-center gap-2 mb-8">
                            <div className="w-8 h-8 bg-white/10 rounded-xl flex items-center justify-center">
                                <FileText size={18} className="text-blue-400" />
                            </div>
                            <h3 className="font-bold text-lg uppercase tracking-tighter">Order Summary</h3>
                        </div>

                        <div className="space-y-6">
                            <div className="flex justify-between items-center py-4 border-b border-white/10">
                                <span className="text-white/60 text-xs font-bold uppercase tracking-widest">Items Count</span>
                                <span className="text-xl font-bold">{formData.items.length}</span>
                            </div>
                            <div className="flex justify-between items-center py-4 border-b border-white/10">
                                <span className="text-white/60 text-xs font-bold uppercase tracking-widest">Total Quantity</span>
                                <span className="text-xl font-bold">
                                    {formData.items.reduce((sum: number, i: any) => sum + (i.quantityOrdered || 0), 0)}
                                </span>
                            </div>
                            <div className="pt-4">
                                <span className="text-white/60 text-xs font-bold uppercase tracking-widest block mb-2">Total Estimated Cost</span>
                                <p className="text-4xl font-black text-blue-400 tracking-tighter">
                                    ${calculateTotalCost().toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </p>
                            </div>

                            <div className="space-y-1.5 pt-6">
                                <label className="text-[10px] font-bold text-white/40 uppercase tracking-widest px-1">Order Notes</label>
                                <textarea
                                    className="w-full h-32 rounded-2xl bg-white/5 border border-white/10 p-4 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all font-medium placeholder:text-white/20"
                                    placeholder="Add any special instructions or internal notes..."
                                    value={formData.orderNotes}
                                    onChange={(e) => setFormData({ ...formData, orderNotes: e.target.value })}
                                />
                            </div>

                            <div className="flex flex-col gap-3 pt-6">
                                <Button
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    className="w-full rounded-2xl h-14 bg-white text-slate-900 hover:bg-slate-100 font-bold text-base gap-2 shadow-lg active:scale-95 transition-all"
                                >
                                    {loading ? (
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-900"></div>
                                    ) : (
                                        <>
                                            <CheckCircle2 size={20} />
                                            {isEdit ? 'Update Order' : 'Create Order'}
                                        </>
                                    )}
                                </Button>
                                {!isEdit && (
                                    <Button
                                        variant="ghost"
                                        className="w-full rounded-2xl h-12 text-white/60 hover:text-white hover:bg-white/5 font-bold gap-2"
                                    >
                                        <HistoryIcon size={18} />
                                        Save as Draft
                                    </Button>
                                )}
                                <Button
                                    variant="ghost"
                                    onClick={onBack}
                                    className="w-full rounded-2xl h-12 text-white/40 hover:text-red-400 hover:bg-red-400/10 font-bold"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewIncomingOrder;
